import { useCallback, useEffect, useState } from "react";
import { FormControlChildProps } from "../form-control";
import Select, { OptionsOrGroups, SingleValue } from "react-select";
import { LabelValue } from "@app/types";

interface CustomReactSelect extends FormControlChildProps {
  placeholder?: string;

  className?: string;

  value?: LabelValue;

  defaultValue?: LabelValue;

  defaultStatus?: undefined | "valid" | "inValid" | "warn";

  errorClassName?: string;

  isSearchable?: boolean;

  isClearable?: boolean;

  initialValueForReset?: LabelValue;

  options: OptionsOrGroups<LabelValue, any>;

  isDisabled?: boolean;
}

function CustomReactSelect({
  id,
  formik,
  placeholder,
  value = { label: "", value: "" },
  defaultValue = undefined,
  name,
  status,
  defaultStatus = undefined,
  errorClassName = "",
  className = "",
  isSearchable = true,
  isClearable = false,
  initialValueForReset,
  onChange,
  options = [],
  isDisabled,
}: CustomReactSelect) {
  const [txValue, setTxValue] = useState<LabelValue | null | undefined>(
    value || defaultValue
  );
  const [_status, changeStatus] = useState(status || defaultStatus);

  const handleChange = useCallback(
    (newValue: SingleValue<LabelValue>) => {
      let finalValue = newValue;

      if (newValue === null && initialValueForReset) {
        finalValue = initialValueForReset;
      }

      setTxValue(finalValue);

      onChange && onChange(finalValue);

      if (formik) {
        formik.setFieldValue(name!, finalValue);
      }

      setTimeout(() => {
        if (formik) {
          formik.setFieldTouched(name!, true);
        }
      });
    },
    [formik, name, onChange, initialValueForReset]
  );

  useEffect(() => {
    if (value?.value) {
      setTxValue(value);
    }
  }, [value?.value]);

  useEffect(() => {
    if (defaultValue) {
      setTxValue(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    changeStatus(status || defaultStatus);
  }, [defaultStatus, status]);

  const isError = _status === "inValid" || _status === "warn";

  return (
    <div>
      <div className={`w-full ${className}`}>
        <Select
          id={id}
          placeholder={placeholder}
          value={txValue && txValue.value ? txValue : undefined}
          onChange={handleChange}
          isSearchable={isSearchable}
          isClearable={isClearable}
          options={options}
          isDisabled={isDisabled}
          styles={{
            control: (baseStyles) => ({
              ...baseStyles,
              backgroundColor: "#f4f5fa",
              height: "52px",
              borderColor: isError ? "#fc4040" : "",
            }),
            clearIndicator: (baseStyles) => ({
              ...baseStyles,
              color: "#dc3545",
              transform: "scale(1.2)",
              cursor: "pointer",
              ":hover": {
                color: "darkred",
              },
            }),
          }}
        />
      </div>
      {isError && (
        <div className={`flex error-color text-xs ${errorClassName}`}>
          {formik &&
            formik.getFieldMeta(name!).error &&
            formik.getFieldMeta(name!).error}
        </div>
      )}
    </div>
  );
}

export default CustomReactSelect;
